import { Link } from '@tanstack/react-router';

import { GarmentMockup } from '@/components/garment-mockup';
import { OrderStatusBadge, QuoteStatusBadge, formatDate } from '@/components/status-timeline';
import { ButtonLink, EmptyState } from '@/components/ui';
import { formatCents, formatCentsOrNone } from '@/lib/money';
import type { GarmentType } from '@/lib/types';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ordersQuery } from '@/queries';

export default function OrdersPage() {
  const { me, orders, quotes } = useSuspenseQuery(ordersQuery).data;
  const user = me.user;

  if (!user) {
    return (
      <div className="mx-auto max-w-[1180px] px-[30px] py-12">
        <h1 className="mb-8 text-[32px] font-extrabold tracking-[-0.03em]">Your orders</h1>
        <EmptyState
          title="Sign in to see your orders"
          body="Orders, quotes and artwork proofs all live in your account."
          action={<ButtonLink href="/auth/sign-in?next=/orders">Sign in</ButtonLink>}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1180px] px-[30px] py-12">
      <h1 className="text-[32px] font-extrabold tracking-[-0.03em] sm:text-[38px]">
        Your orders
      </h1>

      <section className="mt-8" aria-labelledby="orders-heading">
        <h2 id="orders-heading" className="sr-only">
          Orders
        </h2>
        {orders.length === 0 ? (
          <EmptyState
            title="No orders yet"
            body="When you buy something it shows here with a live status timeline."
            action={<ButtonLink href="/shop">Browse the store</ButtonLink>}
          />
        ) : (
          <ul className="space-y-4">
            {orders.map((order) => {
              const first = order.items[0];
              const config = (first?.config ?? {}) as Record<string, unknown>;
              return (
                <li key={order.id}>
                  <Link
                    to="/orders/$ref" params={{ ref: order.ref }}
                    className="flex flex-wrap items-center gap-5 rounded-[10px] border border-line bg-panel p-4 transition-shadow hover:shadow-[var(--shadow-panel)]"
                  >
                    <div className="w-20 shrink-0 rounded-[10px] bg-raised p-1.5">
                      <GarmentMockup
                        type={(config.type as GarmentType) ?? 'tee'}
                        colorHex={(config.color_hex as string) ?? '#cfd3d8'}
                        designUrl={(config.design_url as string) ?? null}
                        placement={{
                          pos_x: (config.pos_x as number) ?? 50,
                          pos_y: (config.pos_y as number) ?? 48,
                          scale: (config.scale as number) ?? 60,
                        }}
                        label={first?.title ?? 'Order item'}
                        className="aspect-square w-full"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-sm font-bold text-brand-ink">{order.ref}</p>
                      <p className="mt-0.5 text-[15px] font-bold">
                        {first?.title}
                        {order.items.length > 1 &&
                          ` and ${order.items.length - 1} more`}
                      </p>
                      <p className="text-sm text-muted">{formatDate(order.created_at)}</p>
                    </div>

                    <OrderStatusBadge status={order.status} />
                    <p className="w-24 text-right font-bold">
                      {formatCents(order.total_cents)}
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {quotes.length > 0 && (
        <section className="mt-14" aria-labelledby="quotes-heading">
          <h2 id="quotes-heading" className="mb-5 text-2xl font-bold tracking-[-0.02em]">
            Your quotes
          </h2>
          <ul className="space-y-4">
            {quotes.map((quote) => {
              const pendingProof = quote.proofs.find((p) => p.status === 'pending');
              return (
                <li key={quote.id}>
                  <Link
                    to="/quotes/$ref" params={{ ref: quote.ref }}
                    className="flex flex-wrap items-center gap-5 rounded-[10px] border border-line bg-panel p-4 transition-shadow hover:shadow-[var(--shadow-panel)]"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-sm font-bold text-brand-ink">{quote.ref}</p>
                      <p className="mt-0.5 text-[15px] font-bold">
                        {quote.qty} units, {quote.method_key}
                      </p>
                      <p className="text-sm text-muted">{formatDate(quote.created_at)}</p>
                    </div>

                    {pendingProof && (
                      <span className="rounded-full border border-brand/30 bg-brand-wash px-2.5 py-1 text-xs font-bold text-brand-ink">
                        Proof waiting on you
                      </span>
                    )}
                    <QuoteStatusBadge status={quote.status} />
                    <p className="w-24 text-right font-bold">
                      {formatCentsOrNone(quote.total_cents ?? quote.est_total_cents)}
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
